import React, {forwardRef} from 'react'
import PropTypes from 'prop-types'
import {FormControl as ChakraFormControl} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * Renders the FormControl DOM root while making Chakra's generated group role optional.
 *
 * @param {Object} props - Props forwarded by Chakra to the root element.
 * @param {string} [props.role] - The role generated by Chakra.
 * @param {boolean} [props.hasGroupRole] - Whether to preserve the generated group role.
 * @param {React.Ref<HTMLDivElement>} ref - Forwarded root element ref.
 * @returns {JSX.Element} The FormControl root element.
 */
const FormControlRoot = forwardRef(({role, hasGroupRole, ...props}, ref) => (
    <div ref={ref} role={hasGroupRole ? role : undefined} {...props} />
))

FormControlRoot.displayName = 'FormControlRoot'
FormControlRoot.propTypes = {
    role: PropTypes.string,
    hasGroupRole: PropTypes.bool
}

/**
 * Wraps Chakra's FormControl without exposing role="group" for a single form control.
 * Set hasGroupRole to true when the control contains a genuine group of related inputs.
 *
 * @param {Object} props - Chakra FormControl props.
 * @param {boolean} [props.hasGroupRole=false] - Whether to render Chakra's group role.
 * @param {React.Ref<HTMLDivElement>} ref - Forwarded root element ref.
 * @returns {JSX.Element} A Chakra FormControl with configurable group semantics.
 */
const FormControl = forwardRef(({hasGroupRole = false, ...props}, ref) => (
    <ChakraFormControl
        ref={ref}
        as={FormControlRoot}
        hasGroupRole={hasGroupRole}
        {...props}
    />
))

FormControl.displayName = 'FormControl'
FormControl.propTypes = {
    hasGroupRole: PropTypes.bool
}

export default FormControl